// Per-route SEO metadata.
//
// Keys are route patterns as declared in App.js. Dynamic segments use the
// same `:param` syntax so lookups can be matched with matchPath.
//
// `noindex: true` marks pages that should never appear in search results
// (confirmation pages, booking forms, and missing pages).

export const SITE_URL = 'https://teamward.ai';
export const SITE_NAME = 'Teamward';
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-default.png`;

// The CloudFront distribution uses an S3 website endpoint as its origin.
// Website endpoints redirect directory-style paths to the trailing-slash form
// (`/about` -> `/about/`). Canonical tags and sitemap entries should therefore
// use the trailing-slash version.
//
// If the origin is ever switched to the S3 REST endpoint with OAC, set this
// to false so canonical URLs and sitemap entries use paths without a slash.
export const TRAILING_SLASH = true;

// Normalizes a pathname to the canonical form for this origin.
export function canonicalPathname(pathname) {
  const bare = pathname.replace(/\/+$/, '');

  if (bare === '') return '/';

  return TRAILING_SLASH ? `${bare}/` : bare;
}

// Strips trailing slashes for ROUTE_META lookups, which are keyed without them.
export function lookupKey(pathname) {
  const bare = pathname.replace(/\/+$/, '');

  return bare === '' ? '/' : bare;
}

// Unknown routes are served through the React application with a 200 response.
// The noindex tag keeps these missing pages out of search results.
export const NOT_FOUND_META = {
  title: 'Page Not Found | Teamward',
  description: 'The page you were looking for could not be found.',
  noindex: true,
};

export const DEFAULT_META = {
  title: 'Teamward | Better Experiences. Stronger Teams.',
  description:
    'Teamward helps companies in Casper and Denver discover team outings, trainings, speakers, retreats, volunteer opportunities, and other team experiences.',
};

export const ROUTE_META = {
  // Redirects to the static /brief.html page; keep the redirect itself out of search.
  '/brief': {
    title: 'The Team Event Brief | Teamward',
    description:
      'A free weekly read for people who plan company events: budgets, attendance trends and ideas that work.',
    noindex: true,
  },

  '/': {
    title: 'Teamward | Team Experiences in Casper & Denver',
    description:
      'Team outings, trainings, speakers, retreats, and volunteer opportunities for Casper and Denver companies. Browse curated experiences or request personalized ideas.',
  },

  '/about': {
    title: 'About Teamward | Team Planning in Casper & Denver',
    description:
      'Learn how Teamward helps managers, HR teams, and executive assistants discover and plan better team experiences without weeks of searching and quote-chasing.',
  },

  '/browse': {
    title: 'Browse Team Experiences in Casper & Denver | Teamward',
    description:
      'Browse team outings, activities, trainings, speakers, retreats, and volunteer opportunities for companies in Casper and Denver.',
  },

  '/partners': {
    title: 'Become a Teamward Partner | List Your Business',
    description:
      'List your Casper or Denver venue, activity, training, or service with Teamward. There is no upfront application or listing fee.',
  },

  '/planning-assistance': {
    title: 'Team Event Planning Assistance | Teamward',
    description:
      'Tell us what your team needs and receive planning options, scope, and pricing. Submitting a request is free; full-service planning is a paid service.',
  },

  '/personalized-ideas': {
    title: 'Get 3 Personalized Team Experience Ideas | Teamward',
    description:
      'Tell us your team size, location, timing, and budget. Teamward will email you three personalized experience ideas at no cost.',
  },

  '/package-request': {
    title: 'Customize Your Team Experience | Teamward',
    description:
      'Request a customizable team experience based on your location, group size, preferred date, goals, and budget. There is no cost to submit a request.',
  },

  '/quote-builder': {
    title: 'Denver Team Event Budget Builder | Teamward',
    description:
      'Plan a Denver team event and see what it could cost. Choose a venue, experience, food, and planning support to get a realistic per-person budget range.',
  },

  '/business/:id': {
    title: 'Team Experience Details | Teamward',
    description:
      'Explore the location, group-size range, pricing information, and details for this Teamward team experience.',
  },

  '/request-booking/:id': {
    title: 'Request Experience Information | Teamward',
    description:
      'Request information about this team experience. Tell us your preferred date and group size, and Teamward will follow up about availability and next steps.',
    noindex: true,
  },

  '/partner-application': {
    title: 'Teamward Partner Application',
    description:
      'Apply to list your Casper or Denver venue, activity, training, or service with Teamward. There is no upfront application or listing fee.',
  },
  
'/privacy': {
  title: 'Privacy Policy | Teamward',
  description:
    'Learn how Teamward collects, uses, shares, and protects information submitted through its website, forms, analytics, and planning services.',
},
  '/terms-conditions': {
    title: 'Terms of Use | Teamward',
    description:
      'Read the terms governing access to and use of the Teamward website, listings, recommendations, forms, and planning services.',
  },

  '/termsAndConditions': {
    title: 'Terms of Use | Teamward',
    description:
      'Read the terms governing access to and use of the Teamward website, listings, recommendations, forms, and planning services.',
    // Duplicate route — point search engines toward the preferred URL.
    canonicalPath: '/terms-conditions',
  },

  '/thank-you': {
    title: 'Thank You | Teamward',
    description: 'Your request has been received.',
    noindex: true,
  },

  '/planning-thank-you': {
    title: 'Thank You | Teamward',
    description: 'Your planning request has been received.',
    noindex: true,
  },

  '/partner-thank-you': {
    title: 'Thank You | Teamward',
    description: 'Your partner application has been received.',
    noindex: true,
  },
};

// Routes that belong in sitemap.xml.
// Keep this list synchronized with scripts/generate-sitemap.js.
export const INDEXABLE_PATHS = Object.keys(ROUTE_META)
  .filter(
    (path) =>
      !ROUTE_META[path].noindex &&
      !path.includes(':') &&
      !ROUTE_META[path].canonicalPath
  )
  .map(canonicalPathname);
